import { VisionaryScene } from "../visionaryControllers/visionaryScene"
import * as THREE from 'three';
import { cameraSyncManager } from './cameraSyncManager';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { globalConfig } from "@/utils/globalConfig";
import { cameraUtils } from "@/utils/cameraUtils";

/**
 * 相机控制器
 * 负责相机的移动、旋转、缩放等操作
 * 负责相机的鼠标控制、键盘控制、惯性控制等操作
 */
export class CameraController {
    private visionaryScene: VisionaryScene;
    private camera: THREE.PerspectiveCamera | null;
    private renderer: any;
    private isMouseDown: boolean = false;
    private enabled: boolean = true; // 是否启用相机控制

    // 更新控件显示的回调函数
    private updateControlsDisplayCallback: (() => void) | null = null;
    // 相机面板UI更新回调函数
    private cameraPanelUpdateCallback: (() => void) | null = null;

    // UI更新节流配置
    private lastUIUpdateTime: number = 0;
    private uiUpdateThrottle: number = 0; // 0表示每帧更新，>0表示节流间隔(ms)
    private pendingUIUpdate: boolean = false;

    // 相机参数限制配置
    private cameraLimits = {
        position: {
            min: { x: null, y: null, z: null },
            max: { x: null, y: null, z: null }
        },
        fov: { min: 10, max: 170 },
        near: { min: 0.01, max: 10 },
        far: { min: 100, max: 10000 },
        moveSpeed: { min: 0.1, max: 5.0 }
    };

    // 鼠标控制相关属性
    private mouseControls = {
        // movementMode: 'orbit' as 'orbit' | 'fps',
        isLeftMouseDown: false,
        isMiddleMouseDown: false,
        isRightMouseDown: false,
        lastMouseX: 0,
        lastMouseY: 0,
        // Orbit 模式参数
        spherical: new THREE.Spherical(),
        target: new THREE.Vector3(),
        // FPS 模式参数
        euler: new THREE.Euler(0, 0, 0, 'YXZ'),
        // 轴反转配置（可选）
        invertX: false,
        invertY: false,
        // 速度参数
        moveSpeed: 1.0,
        wheelSpeed: 1.0,
        dragSpeed: 1.0,
        inertiaFactor: 0.00,
        // 惯性参数
        velocity: new THREE.Vector3(),
        rotationVelocity: new THREE.Vector2(),
        // FPS 模式键盘控制
        keys: {
            forward: false,    // W
            backward: false,   // S
            left: false,       // A
            right: false,      // D
            up: false,         // E - 世界坐标上升
            down: false        // Q - 世界坐标下降
        }
    };
    private boundMouseDown: ((event: MouseEvent) => void) | null = null;
    private boundMouseUp: ((event: MouseEvent) => void) | null = null;
    private boundMouseMove: ((event: MouseEvent) => void) | null = null;
    private boundMouseLeave: (() => void) | null = null;
    private boundWheel: ((event: WheelEvent) => void) | null = null;
    private boundContextMenu: ((event: MouseEvent) => void) | null = null;
    private boundMouseEnter: (() => void) | null = null;
    private boundKeyDown: ((event: KeyboardEvent) => void) | null = null;
    private boundKeyUp: ((event: KeyboardEvent) => void) | null = null;

    constructor(visionaryScene: VisionaryScene) {
        this.visionaryScene = visionaryScene;
        this.camera = visionaryScene.getCamera();
        this.renderer = visionaryScene.getRenderer();

        this.init();

        this.visionaryScene.onGizmoManagerReady((gizmo) => {
            gizmo.setCameraControls(this);
        });

        const gizmoManager = this.visionaryScene.getGizmoManager();
        if (gizmoManager) {
            gizmoManager.setCameraControls(this);
        }
    }

    init(): void {
        // 检查renderer是否已初始化
        if (!this.renderer) {
            console.warn('Renderer not initialized yet, skipping mouse controls setup');
            return;
        }
        // 设置鼠标控制
        this.setupMouseControls();
    }

    /**
     * 设置更新控件显示的回调函数
     * @param callback 回调函数
     */
    setUpdateControlsDisplayCallback(callback: (() => void) | null): void {
        this.updateControlsDisplayCallback = callback;
    }

    /**
     * 设置相机面板UI更新回调函数
     * @param callback 回调函数
     */
    setCameraPanelUpdateCallback(callback: (() => void) | null): void {
        this.cameraPanelUpdateCallback = callback;
    }

    /**
     * 设置UI更新节流间隔
     * @param ms 节流间隔（毫秒），0表示每帧更新，推荐值：0(默认), 100, 150, 200
     */
    public setUIUpdateThrottle(ms: number): void {
        this.uiUpdateThrottle = Math.max(0, ms);
        console.log(`相机UI更新间隔已设置为: ${this.uiUpdateThrottle}ms ${this.uiUpdateThrottle === 0 ? '(每帧更新)' : ''}`);
    }

    /**
     * 限制数值在指定范围内
     */
    private clamp(value: number, min: number, max: number): number {
        if (min === undefined || min === null) {
            min = -Infinity;
        }
        if (max === undefined || max === null) {
            max = Infinity;
        }
        return Math.max(min, Math.min(max, value));
    }

    /**
     * 限制位置在安全范围内
     */
    private clampPosition(position: THREE.Vector3): THREE.Vector3 {
        // position.x = this.clamp(position.x,
        //     this.cameraLimits.position.min.x,
        //     this.cameraLimits.position.max.x);
        // position.y = this.clamp(position.y,
        //     this.cameraLimits.position.min.y,
        //     this.cameraLimits.position.max.y);
        // position.z = this.clamp(position.z,
        //     this.cameraLimits.position.min.z,
        //     this.cameraLimits.position.max.z);
        return position;
    }

    /**
     * 限制FOV在安全范围内
     */
    private clampFOV(fov: number): number {
        return this.clamp(fov, this.cameraLimits.fov.min, this.cameraLimits.fov.max);
    }

    /**
     * 限制near平面在安全范围内
     */
    private clampNear(near: number): number {
        return this.clamp(near, this.cameraLimits.near.min, this.cameraLimits.near.max);
    }

    /**
     * 限制far平面在安全范围内
     */
    private clampFar(far: number): number {
        return this.clamp(far, this.cameraLimits.far.min, this.cameraLimits.far.max);
    }

    /**
     * 更新相机参数限制
     */
    public setCameraLimits(limits: Partial<typeof this.cameraLimits>): void {
        this.cameraLimits = { ...this.cameraLimits, ...limits };
        console.log('相机参数限制已更新:', this.cameraLimits);
    }

    /**
     * 获取当前相机参数限制
     */
    public getCameraLimits() {
        return { ...this.cameraLimits };
    }

    /**
     * 通知相机已更新（支持节流配置）
     * @param forceUpdate 强制立即更新，忽略节流
     */
    private notifyCameraUpdate(forceUpdate: boolean = false): void {
        // 立即更新ViewHelper（轻量级）
        if (this.updateControlsDisplayCallback) {
            this.updateControlsDisplayCallback();
        }

        // 相机面板UI更新（根据配置决定是否节流）
        if (this.uiUpdateThrottle === 0) {
            // 每帧更新模式（默认）
            if (this.cameraPanelUpdateCallback) {
                this.cameraPanelUpdateCallback();
            }
        } else {
            // 节流模式
            const now = Date.now();
            if (forceUpdate || now - this.lastUIUpdateTime > this.uiUpdateThrottle) {
                if (this.cameraPanelUpdateCallback) {
                    this.cameraPanelUpdateCallback();
                }
                this.lastUIUpdateTime = now;
                this.pendingUIUpdate = false;
            } else {
                // 标记有待处理的更新
                this.pendingUIUpdate = true;
            }
        }
    }

    public getThreeScene(): VisionaryScene {
        return this.visionaryScene;
    }
    public getCamera(): THREE.PerspectiveCamera | null {
        return this.camera;
    }
    public getCameraPosition(): THREE.Vector3 | null {
        return this.camera?.position || null;
    }
    public getRendererDomElement(): HTMLElement {
        return this.renderer.domElement;
    }
    public getRenderer(): any {
        return this.renderer;
    }

    /**
     * 设置是否启用相机控制
     * @param enabled 是否启用
     */
    public setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        // 如果禁用，重置鼠标状态
        if (!enabled) {
            this.isMouseDown = false;
            this.mouseControls.isLeftMouseDown = false;
            this.mouseControls.isMiddleMouseDown = false;
            this.mouseControls.isRightMouseDown = false;
        }
    }

    /**
     * 获取是否启用相机控制
     * @returns 是否启用
     */
    public getEnabled(): boolean {
        return this.enabled;
    }

    /**
     * 更新视口尺寸
     */
    public updateViewport(width: number, height: number): void {
        this.visionaryScene.setViewport(width, height);
    }

    /**
     * 销毁相机控制
     */
    destroy(): void {
        // 清理鼠标事件监听
        this.removeMouseControls();
    }

    /**
     * 设置鼠标控制 - 集成先进的相机控制系统
     */
    private setupMouseControls(): void {
        if (!this.renderer) return;

        const canvas = this.renderer.domElement;
        if (!canvas) return;

        // 初始化相机模式
        this.initializeCameraMode();

        // 绑定事件处理函数
        this.boundMouseDown = this.onMouseDown.bind(this);
        this.boundMouseUp = this.onMouseUp.bind(this);
        this.boundMouseMove = this.onMouseMove.bind(this);
        this.boundMouseLeave = this.onMouseLeave.bind(this);
        this.boundMouseEnter = this.onMouseEnter.bind(this);
        this.boundWheel = this.onWheel.bind(this);
        this.boundContextMenu = this.onContextMenu.bind(this);

        // 添加事件监听
        canvas.addEventListener('mousedown', this.boundMouseDown);
        canvas.addEventListener('mouseup', this.boundMouseUp);
        canvas.addEventListener('mousemove', this.boundMouseMove);
        canvas.addEventListener('mouseenter', this.boundMouseEnter);
        canvas.addEventListener('mouseleave', this.boundMouseLeave);
        canvas.addEventListener('wheel', this.boundWheel, { passive: false });
        canvas.addEventListener('contextmenu', this.boundContextMenu);

        // 添加全局 mouseup 事件监听（防止在画布外释放鼠标）
        document.addEventListener('mouseup', this.boundMouseUp);

        // 添加键盘事件监听（用于FPS模式）
        this.boundKeyDown = this.onKeyDown.bind(this);
        this.boundKeyUp = this.onKeyUp.bind(this);
        window.addEventListener('keydown', this.boundKeyDown!);
        window.addEventListener('keyup', this.boundKeyUp!);

        // 启动惯性更新循环
        this.startInertiaLoop();
        // 启动FPS移动循环
        this.startFPSMovementLoop();
    }

    /**
     * 移除鼠标控制
     */
    private removeMouseControls(): void {
        const canvas = this.renderer.domElement;
        if (!canvas) return;

        if (this.boundMouseDown) {
            canvas.removeEventListener('mousedown', this.boundMouseDown);
            this.boundMouseDown = null;
        }
        if (this.boundMouseUp) {
            canvas.removeEventListener('mouseup', this.boundMouseUp);
            document.removeEventListener('mouseup', this.boundMouseUp);
            this.boundMouseUp = null;
        }
        if (this.boundMouseMove) {
            canvas.removeEventListener('mousemove', this.boundMouseMove);
            this.boundMouseMove = null;
        }
        if (this.boundMouseEnter) {
            canvas.removeEventListener('mouseenter', this.boundMouseEnter);
            this.boundMouseEnter = null;
        }
        if (this.boundMouseLeave) {
            canvas.removeEventListener('mouseleave', this.boundMouseLeave);
            this.boundMouseLeave = null;
        }
        if (this.boundWheel) {
            canvas.removeEventListener('wheel', this.boundWheel);
            this.boundWheel = null;
        }
        if (this.boundContextMenu) {
            canvas.removeEventListener('contextmenu', this.boundContextMenu);
            this.boundContextMenu = null;
        }
        if (this.boundKeyDown) {
            window.removeEventListener('keydown', this.boundKeyDown);
            this.boundKeyDown = null;
        }
        if (this.boundKeyUp) {
            window.removeEventListener('keyup', this.boundKeyUp);
            this.boundKeyUp = null;
        }
    }

    /**
     * 初始化相机模式
     */
    private initializeCameraMode(): void {
        if (!this.camera) return;
        if (globalConfig.mouseMovementMode === 'orbit') {
            // 初始化 Orbit 模式
            this.mouseControls.target.set(0, 0, 0);
            this.mouseControls.spherical.setFromVector3(this.camera.position.clone().sub(this.mouseControls.target));
        } else {
            // 初始化 FPS 模式
            this.mouseControls.euler.setFromQuaternion(this.camera.quaternion);
        }
    }

    /**
     * 从当前相机状态同步内部状态（用于外部直接修改相机后的同步）
     * 当相机位置被外部直接修改（如 ViewHelper 跳转视角）后，需要调用此方法同步内部状态
     */
    public syncFromCamera(): void {
        if (!this.camera) return;

        if (globalConfig.mouseMovementMode === 'orbit') {
            // 同步 Orbit 模式的状态
            // 保持目标点在原点（可以根据需要修改为场景中心）
            this.mouseControls.target.set(0, 0, 0);
            // 从当前相机位置计算球坐标系参数
            const offset = this.camera.position.clone().sub(this.mouseControls.target);
            this.mouseControls.spherical.setFromVector3(offset);
        } else {
            // 同步 FPS 模式的状态
            this.mouseControls.euler.setFromQuaternion(this.camera.quaternion);
        }
        console.log("syncFromCamera", this.camera);
    }

    public syncFromGizmoHelper(gizmoPosition: { x: number, y: number, z: number }): void {
        if (!this.camera) return;
        this.syncFromCamera();
        const cameraParams = {
            position: this.camera.position,
            quaternion: this.camera.quaternion,
            fov: this.camera.fov,
            near: this.camera.near,
            far: this.camera.far
        };
        cameraUtils.onGizmoChange(this.visionaryScene.getId(),cameraParams);
    }

    public syncFromValue(position: { x: number, y: number, z: number }, quaternion: { x: number, y: number, z: number, w: number }): void {
        if (!this.camera) return;
        this.camera.position.set(position.x, position.y, position.z);
        this.camera.quaternion.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
        this.camera.updateMatrixWorld();
        this.initializeCameraMode();
        this.syncFromCamera();
    }

    public syncGizmoHelperFromCamera(gizmoPosition: { x: number, y: number, z: number }): void {
        if (!this.camera) return;
        this.camera.position.set(gizmoPosition.x, gizmoPosition.y, gizmoPosition.z);
        this.camera.updateMatrixWorld();
        this.initializeCameraMode();
    }

    // public setCameraMode(mode: string): void {
    //     globalConfig.mouseMovementMode = mode as 'orbit' | 'fps';
    //     this.initializeCameraMode();
    // }

    public setMouseControls(key: string, value: any): void {
        if (key === 'moveSpeed') {
            // 限制速度在安全范围内
            // value = this.clamp(value, this.cameraLimits.moveSpeed.min, this.cameraLimits.moveSpeed.max);
        }
        (this.mouseControls as any)[key] = value;
    }

    /**
     * 从UI更新相机位置
     */
    public updateCameraPositionFromUI(x: number, y: number, z: number): void {
        if (!this.camera) return;

        // UI已经限制了输入，这里再次确保安全
        const position = new THREE.Vector3(x, y, z);
        this.clampPosition(position);

        this.camera.position.copy(position);

        // 更新内部控制状态
        this.updateInternalStateFromCamera();

        // 触发相机同步
        cameraSyncManager.syncCameras(this);

        this.initializeCameraMode();
    }

    /**
     * 更新相机FOV
     */
    public updateCameraFOV(fov: number): void {
        if (!this.camera) return;

        // 限制FOV
        this.camera.fov = this.clampFOV(fov);
        this.camera.updateProjectionMatrix();

        // 触发相机同步
        cameraSyncManager.syncCameras(this);
    }

    /**
     * 更新相机近平面
     */
    public updateCameraNear(near: number): void {
        if (!this.camera) return;

        // 限制near平面
        this.camera.near = this.clampNear(near);
        this.camera.updateProjectionMatrix();

        // 触发相机同步
        cameraSyncManager.syncCameras(this);
    }

    /**
     * 更新相机远平面
     */
    public updateCameraFar(far: number): void {
        if (!this.camera) return;

        // 限制far平面
        this.camera.far = this.clampFar(far);
        this.camera.updateProjectionMatrix();

        // 触发相机同步
        cameraSyncManager.syncCameras(this);
    }

    public updateIntertiaFactor(factor: number): void {
        this.mouseControls.inertiaFactor = factor;
    }
    public updateMoveSpeed(speed: number): void {
        this.mouseControls.moveSpeed = speed;
    }
    public updateWheelSpeed(speed: number): void {
        this.mouseControls.wheelSpeed = speed;
    }
    public updateDragSpeed(speed: number): void {
        this.mouseControls.dragSpeed = speed;
    }

    public resetCameraLookAt(x: number, y: number, z: number): void {
        if (!this.camera) return;
        this.camera.lookAt(x, y, z);
        this.camera.updateMatrixWorld();
        // 触发相机同步
        cameraSyncManager.syncCameras(this);
        this.initializeCameraMode();
    }

    /**
     * 鼠标按下事件处理
     */
    private onMouseDown(event: MouseEvent): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        event.preventDefault();
        // console.log("on MouseDown----camera tools -------");
        this.isMouseDown = true; // 记录鼠标按下状态
        this.isMouseDownOnCanvas = true; // 标记鼠标在该canvas上按下
        this.mouseControls.lastMouseX = event.clientX;
        this.mouseControls.lastMouseY = event.clientY;

        switch (event.button) {
            case 0: // 左键 - 旋转
                this.mouseControls.isLeftMouseDown = true;
                break;
            case 1: // 中键 - 缩放
                this.mouseControls.isMiddleMouseDown = true;
                break;
            case 2: // 右键 - 平移
                this.mouseControls.isRightMouseDown = true;
                break;
        }
    }

    /**
     * 鼠标抬起事件处理
     */
    private onMouseUp(event: MouseEvent): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        event.preventDefault();
        // console.log("on MouseUp----camera tools -------");
        this.isMouseDown = false;
        
        // 鼠标释放时，如果鼠标仍在canvas上，保持isMouseDownOnCanvas为true
        // 如果鼠标已离开canvas（通过mouseleave事件），isMouseOverCanvas会是false，此时isMouseDownOnCanvas应该为false
        // 这里我们根据当前鼠标是否在canvas上来决定
        if (!this.isMouseOverCanvas) {
            this.isMouseDownOnCanvas = false;
        }
        // 如果鼠标仍在canvas上，isMouseDownOnCanvas会在下次鼠标按下时设置为true
        switch (event.button) {
            case 0:
                this.mouseControls.isLeftMouseDown = false;
                break;
            case 1:
                this.mouseControls.isMiddleMouseDown = false;
                break;
            case 2:
                this.mouseControls.isRightMouseDown = false;
                break;
        }
    }

    /**
     * 鼠标移动事件处理
     */
    private onMouseMove(event: MouseEvent): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        // 更新鼠标悬停状态
        if (this.renderer && this.renderer.domElement) {
            const canvas = this.renderer.domElement;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX;
            const y = event.clientY;
            
            // 检查鼠标是否在canvas区域内
            this.isMouseOverCanvas = (x >= rect.left && x <= rect.right && 
                                     y >= rect.top && y <= rect.bottom);
        }

        if (!this.isMouseDown) return;
        const deltaX = event.clientX - this.mouseControls.lastMouseX;
        const deltaY = event.clientY - this.mouseControls.lastMouseY;
        // console.log("on MouseMove----camera tools -------");
        if (globalConfig.mouseMovementMode === 'orbit') {
            // Orbit 模式
            if (this.mouseControls.isLeftMouseDown) {
                // 左键拖拽 - 绕目标点旋转
                this.handleOrbitRotation(deltaX, deltaY);
            } else if (this.mouseControls.isRightMouseDown) {
                // 右键拖拽 - 平移目标点
                this.handleOrbitPan(deltaX, deltaY);
            }
        } else {
            // FPS 模式
            if (this.mouseControls.isRightMouseDown) {
                // 右键拖拽 - 旋转视角
                this.handleFPSRotation(deltaX, deltaY);
            }
            // FPS 模式下左键不响应
        }

        this.mouseControls.lastMouseX = event.clientX;
        this.mouseControls.lastMouseY = event.clientY;

        this.updateCameraTransform();
    }

    /**
     * 鼠标进入事件处理
     */
    private onMouseEnter(): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        this.isMouseOverCanvas = true; // 标记鼠标在canvas上
    }

    /**
     * 鼠标离开事件处理
     */
    private onMouseLeave(): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        this.isMouseDown = false;
        this.isMouseOverCanvas = false; // 鼠标离开canvas
        this.isMouseDownOnCanvas = false; // 鼠标离开时清除按下状态
        // console.log("on MouseLeave----camera tools -------");
        this.mouseControls.isLeftMouseDown = false;
        this.mouseControls.isMiddleMouseDown = false;
        this.mouseControls.isRightMouseDown = false;
    }

    /**
     * 鼠标滚轮事件处理
     */
    private onWheel(event: WheelEvent): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;
        if (globalConfig.mouseMovementMode !== 'orbit') return; // 只有orbit模式下才处理滚轮事件

        event.preventDefault();
        const delta = event.deltaY;
        this.handleCameraZoom(delta);
        this.updateCameraTransform();
    }

    /**
     * 右键菜单事件处理
     */
    private onContextMenu(event: MouseEvent): void {
        event.preventDefault();
    }

    // 记录鼠标是否在当前canvas上，用于判断键盘事件应该由哪个canvas处理
    private isMouseOverCanvas: boolean = false;
    private isMouseDownOnCanvas: boolean = false;

    /**
     * 检查当前键盘事件是否应该被该CameraController实例处理
     * 通过检查鼠标是否在对应的canvas区域内，或canvas是否获得焦点来判断
     * @returns 如果应该处理该事件返回true，否则返回false
     */
    private shouldHandleKeyboardEvent(): boolean {
        if (!this.renderer || !this.renderer.domElement) {
            return false;
        }

        const canvas = this.renderer.domElement;

        // 方法1：检查canvas是否获得焦点（通过检查activeElement是否为canvas或其子元素）
        const activeElement = document.activeElement;
        if (activeElement === canvas || canvas.contains(activeElement)) {
            return true;
        }

        // 方法2：检查鼠标是否在canvas上（通过跟踪鼠标进入/离开事件）
        if (this.isMouseOverCanvas) {
            return true;
        }

        // 方法3：检查鼠标是否在canvas上按下（如果鼠标正在canvas上按下，说明用户正在操作该canvas）
        if (this.isMouseDownOnCanvas) {
            return true;
        }

        // 如果以上条件都不满足，说明该canvas不是当前活动的canvas，不处理键盘事件
        return false;
    }

    /**
     * 键盘按下事件处理
     */
    private onKeyDown(event: KeyboardEvent): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        // 检查该键盘事件是否应该由该CameraController实例处理
        if (!this.shouldHandleKeyboardEvent()) return;

        if (globalConfig.mouseMovementMode !== 'fps') return;

        switch (event.code) {
            case 'KeyW':
                this.mouseControls.keys.forward = true;
                break;
            case 'KeyS':
                this.mouseControls.keys.backward = true;
                break;
            case 'KeyA':
                this.mouseControls.keys.left = true;
                break;
            case 'KeyD':
                this.mouseControls.keys.right = true;
                break;
            case 'KeyE':
                this.mouseControls.keys.up = true;
                break;
            case 'KeyQ':
                this.mouseControls.keys.down = true;
                break;
        }
    }

    /**
     * 键盘释放事件处理
     */
    private onKeyUp(event: KeyboardEvent): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        // 检查该键盘事件是否应该由该CameraController实例处理
        if (!this.shouldHandleKeyboardEvent()) return;

        // if (globalConfig.mouseMovementMode !== 'fps') return;

        if (globalConfig.mouseMovementMode === 'fps') {
            switch (event.code) {
                case 'KeyW':
                    this.mouseControls.keys.forward = false;
                    break;
                case 'KeyS':
                    this.mouseControls.keys.backward = false;
                    break;
                case 'KeyA':
                    this.mouseControls.keys.left = false;
                    break;
                case 'KeyD':
                    this.mouseControls.keys.right = false;
                    break;
                case 'KeyE':
                    this.mouseControls.keys.up = false;
                    break;
                case 'KeyQ':
                    this.mouseControls.keys.down = false;
                    break;
            }

            // 检查是否所有移动键都已释放
            const allKeysReleased = !this.mouseControls.keys.forward &&
                !this.mouseControls.keys.backward &&
                !this.mouseControls.keys.left &&
                !this.mouseControls.keys.right &&
                !this.mouseControls.keys.up &&
                !this.mouseControls.keys.down;

            if (allKeysReleased && this.pendingUIUpdate) {
                // 强制更新UI确保同步
                this.notifyCameraUpdate(true);
            }
        } else if (globalConfig.mouseMovementMode === 'orbit') {
            // console.log("press key in orbit mode", event.code);
            switch (event.code) {
                case 'KeyG':
                    console.log("press G in orbit mode");
                    this.visionaryScene.setGizmoMode('translate')
                    break;
                case 'KeyR':
                    console.log("press R in orbit mode");
                    this.visionaryScene.setGizmoMode('rotate')
                    break;
                case 'KeyS':
                    console.log("press S in orbit mode");
                    this.visionaryScene.setGizmoMode('scale')
                    break;
                case 'Escape':
                    console.log("press Escape in orbit mode");
                    this.visionaryScene.setGizmoMode('normal')
                    break;
            }
        }
    }

    /**
     * Orbit 模式旋转处理
     */
    private handleOrbitRotation(deltaX: number, deltaY: number): void {
        const rotationSpeed = this.mouseControls.dragSpeed * 0.005;
        const ix = this.mouseControls.invertX ? -1 : 1;
        const iy = this.mouseControls.invertY ? -1 : 1;

        this.mouseControls.spherical.theta -= (deltaX * ix) * rotationSpeed;
        this.mouseControls.spherical.phi -= (deltaY * iy) * rotationSpeed;
        // this.mouseControls.spherical.phi = Math.max(1e-6, Math.min(Math.PI - 1e-6, this.mouseControls.spherical.phi));

        // 添加惯性
        this.mouseControls.rotationVelocity.set((deltaX * ix) * rotationSpeed, -(deltaY * iy) * rotationSpeed);
    }

    /**
     * FPS 模式旋转处理
     */
    private handleFPSRotation(deltaX: number, deltaY: number): void {
        if (!this.camera) return;

        const rotationSpeed = this.mouseControls.dragSpeed * 0.005;
        const ix = this.mouseControls.invertX ? -1 : 1;
        const iy = this.mouseControls.invertY ? -1 : 1;

        this.mouseControls.euler.setFromQuaternion(this.camera.quaternion);
        this.mouseControls.euler.y -= (deltaX * ix) * rotationSpeed;
        this.mouseControls.euler.x -= (deltaY * iy) * rotationSpeed;
        this.mouseControls.euler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.mouseControls.euler.x));

        // 添加惯性
        this.mouseControls.rotationVelocity.set(-(deltaY * iy) * rotationSpeed, (deltaX * ix) * rotationSpeed);
    }

    /**
     * Orbit 模式平移处理
     */
    private handleOrbitPan(deltaX: number, deltaY: number): void {
        if (!this.camera) return;

        const panSpeed = this.mouseControls.moveSpeed * 0.01;
        const offset = new THREE.Vector3();
        offset.copy(this.camera.position).sub(this.mouseControls.target);

        const targetDistance = offset.length();
        const panLeft = new THREE.Vector3();
        const panUp = new THREE.Vector3();

        panLeft.setFromMatrixColumn(this.camera.matrix, 0);
        panUp.setFromMatrixColumn(this.camera.matrix, 1);

        panLeft.multiplyScalar(-deltaX * panSpeed * targetDistance);
        // 修复y方向：鼠标向上应该向上平移，鼠标向下应该向下平移
        // panUp.multiplyScalar(-deltaY * panSpeed * targetDistance);
        panUp.multiplyScalar(deltaY * panSpeed * targetDistance);

        this.mouseControls.target.add(panLeft).add(panUp);

        // 应用目标点位置限制
        this.clampPosition(this.mouseControls.target);

        // 添加惯性
        this.mouseControls.velocity.add(panLeft.add(panUp).multiplyScalar(this.mouseControls.inertiaFactor));
    }

    /**
     * FPS 模式 WASD 移动处理
     */
    private handleFPSMovement(): void {
        // 如果相机控制被禁用，不处理事件
        if (!this.enabled) return;

        if (globalConfig.mouseMovementMode !== 'fps') return;
        if (!this.mouseControls.isRightMouseDown) return; // 只有按下右键时才caiyou用WASD
        if (!this.camera) return;

        const moveSpeed = this.mouseControls.moveSpeed * 0.1;

        // 使用相机的局部坐标系
        const forward = new THREE.Vector3();
        const right = new THREE.Vector3();
        const up = new THREE.Vector3();

        // 获取相机的局部坐标系方向向量
        // 相机的局部Z轴指向负方向（Three.js相机默认看向-Z）
        this.camera.getWorldDirection(forward); // 这是相机看向的方向（即局部-Z方向）
        // forward 现在指向相机看向的方向（前进应该沿这个方向）

        // 获取相机的上方向（局部Y轴）
        up.setFromMatrixColumn(this.camera.matrixWorld, 1); // 从矩阵的第二列提取上方向
        up.normalize();

        // 直接从相机的矩阵中提取右方向（局部X轴），第一列是右方向
        right.setFromMatrixColumn(this.camera.matrixWorld, 0);
        right.normalize();

        const movement = new THREE.Vector3();

        // W/S: 沿相机看向的方向（前进/后退）
        // forward 已经是指向相机看向的方向，所以W应该沿+forward移动，S应该沿-forward移动
        if (this.mouseControls.keys.forward) {
            movement.add(forward.clone().multiplyScalar(moveSpeed));
        }
        if (this.mouseControls.keys.backward) {
            movement.add(forward.clone().multiplyScalar(-moveSpeed));
        }

        // A/D: 沿相机的局部X轴（左右移动）
        // 修复方向：A键向左移动（-right），D键向右移动（+right）
        if (this.mouseControls.keys.left) {
            movement.add(right.clone().multiplyScalar(-moveSpeed)); // A键：向左移动
        }
        if (this.mouseControls.keys.right) {
            movement.add(right.clone().multiplyScalar(+moveSpeed)); // D键：向右移动
        }

        // E/Q: 沿世界坐标系的Y轴（上升/下降）
        if (this.mouseControls.keys.up) {
            movement.add(new THREE.Vector3(0, moveSpeed, 0)); // E键：世界坐标上升
        }
        if (this.mouseControls.keys.down) {
            movement.add(new THREE.Vector3(0, -moveSpeed, 0)); // Q键：世界坐标下降
        }

        if (movement.length() > 0) {
            this.camera.position.add(movement);

            // 应用位置限制
            this.clampPosition(this.camera.position);

            // 添加惯性
            this.mouseControls.velocity.add(movement.multiplyScalar(this.mouseControls.inertiaFactor));

            // 触发相机同步
            cameraSyncManager.syncCameras(this);

            // 通知相机更新（支持节流配置）
            this.notifyCameraUpdate(false);
        }
    }

    /**
     * 处理相机缩放
     */
    private handleCameraZoom(delta: number): void {
        if (!this.camera) return;

        const zoomSpeed = this.mouseControls.wheelSpeed * 0.1;
        if (globalConfig.mouseMovementMode === 'orbit') {
            // Orbit 模式调整距离
            this.mouseControls.spherical.radius *= (1 + delta * zoomSpeed * 0.001);
            // this.mouseControls.spherical.radius = Math.max(0.1, Math.min(100, this.mouseControls.spherical.radius)); // 取消对滚轮缩放限制
        } else { // FPS模式下不处理滚轮缩放
            // // FPS 模式调整FOV
            // this.camera.fov += delta * zoomSpeed * 0.5;

            // // 应用FOV限制
            // this.camera.fov = this.clampFOV(this.camera.fov);

            // this.camera.updateProjectionMatrix();

            // // 同步UI控件（通过统一的更新方法）
            // this.notifyCameraUpdate(false);
        }
    }

    /**
     * 更新相机变换
     */
    private updateCameraTransform(skipUIUpdate = false): void {
        if (!this.camera) return;

        if (globalConfig.mouseMovementMode === 'orbit') {
            // Orbit 模式更新相机位置
            const position = new THREE.Vector3();
            position.setFromSpherical(this.mouseControls.spherical);
            position.add(this.mouseControls.target);

            this.camera.position.copy(position);
            this.camera.lookAt(this.mouseControls.target);
        } else {
            // FPS 模式更新相机旋转
            this.camera.quaternion.setFromEuler(this.mouseControls.euler);
        }

        // 只在不跳过UI更新时才同步UI控件显示
        if (!skipUIUpdate) {
            this.notifyCameraUpdate(false);
        }

        // 触发相机同步
        cameraSyncManager.syncCameras(this);
    }

    /**
     * 启动惯性循环
     */
    private startInertiaLoop(): void {
        const animate = () => {
            if (this.mouseControls.inertiaFactor > 0) {
                // 应用惯性
                if (globalConfig.mouseMovementMode === 'orbit') {
                    this.mouseControls.spherical.theta += this.mouseControls.rotationVelocity.x;
                    this.mouseControls.spherical.phi += this.mouseControls.rotationVelocity.y;

                    this.mouseControls.target.add(this.mouseControls.velocity);
                } else {
                    this.mouseControls.euler.y += this.mouseControls.rotationVelocity.y;
                    this.mouseControls.euler.x += this.mouseControls.rotationVelocity.x;

                    if (this.camera) {
                        this.camera.position.add(this.mouseControls.velocity);
                    }
                }

                // 阻尼衰减
                this.mouseControls.rotationVelocity.multiplyScalar(1 - this.mouseControls.inertiaFactor);
                this.mouseControls.velocity.multiplyScalar(1 - this.mouseControls.inertiaFactor);

                // 如果速度太小，停止惯性
                if (this.mouseControls.rotationVelocity.length() > 0.001 || this.mouseControls.velocity.length() > 0.001) {
                    this.updateCameraTransform(true); // 跳过UI更新，避免覆盖用户输入
                }
            }

            requestAnimationFrame(animate);
        };
        animate();
    }

    /**
     * 启动FPS移动循环
     */
    private startFPSMovementLoop(): void {
        const animate = () => {
            // 处理FPS模式WASD移动
            this.handleFPSMovement();
            requestAnimationFrame(animate);
        };
        animate();
    }


    /**
     * 从相机状态更新内部控制状态
     */
    public updateInternalStateFromCamera(): void {
        if (!this.camera) return;

        if (globalConfig.mouseMovementMode === 'orbit') {
            // 更新Orbit模式的spherical和target
            // 根据相机的当前位置和朝向重新计算target点
            const direction = new THREE.Vector3();
            this.camera.getWorldDirection(direction);

            // 使用当前的spherical.radius作为到target的距离
            const distance = this.mouseControls.spherical.radius;

            // 新的target = 相机位置 + 朝向 * 距离
            this.mouseControls.target.copy(this.camera.position).add(direction.multiplyScalar(distance));

            // 重新从相机位置计算spherical坐标
            const offset = new THREE.Vector3();
            offset.copy(this.camera.position).sub(this.mouseControls.target);
            this.mouseControls.spherical.setFromVector3(offset);
        } else {
            // 更新FPS模式的euler
            this.mouseControls.euler.setFromQuaternion(this.camera.quaternion);
        }
        this.updateCameraProjectionMatrix()
    }

    public updateCameraProjectionMatrix(): void {
        this.visionaryScene.updateCameraProjectionMatrix(() => {
            // 这里可以添加其他更新逻辑
        });
    }

    /**
     * 应用外部同步的相机状态
     */
    public applyCameraState(sourceCamera: THREE.PerspectiveCamera): void {
        if (!this.camera) return;

        // 同步基本变换参数
        this.camera.position.copy(sourceCamera.position);
        this.camera.rotation.copy(sourceCamera.rotation);
        this.camera.scale.copy(sourceCamera.scale);
        this.camera.quaternion.copy(sourceCamera.quaternion);

        // 同步投影参数
        this.camera.fov = sourceCamera.fov;
        // 不同步 aspect，让每个相机基于自己的视口尺寸计算
        // this.camera.aspect = sourceCamera.aspect;
        this.camera.near = sourceCamera.near;
        this.camera.far = sourceCamera.far;

        // 更新投影矩阵
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();

        // 同步控制器内部状态（重要！）
        this.syncInternalStateFromCamera(sourceCamera);

        // 更新UI控件显示
        this.notifyCameraUpdate(false);
    }

    /**
     * 从源相机同步内部状态（避免循环调用）
     */
    private syncInternalStateFromCamera(sourceCamera: THREE.PerspectiveCamera): void {
        if (!this.camera) return;

        if (globalConfig.mouseMovementMode === 'orbit') {
            // 同步 Orbit 模式的状态
            // 计算从源相机到目标点的距离
            const direction = new THREE.Vector3();
            sourceCamera.getWorldDirection(direction);

            // 使用当前距离或计算新距离
            const currentDistance = this.mouseControls.spherical.radius;

            // 更新 target 点
            this.mouseControls.target.copy(sourceCamera.position).add(direction.multiplyScalar(currentDistance));

            // 更新 spherical 坐标
            const offset = new THREE.Vector3();
            offset.copy(sourceCamera.position).sub(this.mouseControls.target);
            this.mouseControls.spherical.setFromVector3(offset);
        } else {
            // 同步 FPS 模式的状态
            this.mouseControls.euler.setFromQuaternion(sourceCamera.quaternion);
        }
    }

}